import React, { useState, useEffect, useCallback } from 'react';

const SCORE_LABELS = [
  { value: 1, label: 'Initial',    color: '#C62828' },
  { value: 2, label: 'Developing', color: '#F57F17' },
  { value: 3, label: 'Defined',    color: '#1565C0' },
  { value: 4, label: 'Managed',    color: '#2E7D32' },
  { value: 5, label: 'Optimized',  color: '#0078D4' },
];

const scoreColor = s => (SCORE_LABELS.find(l => l.value === s) || {}).color || '#B0BAC5';

const MIN_NOTES_LENGTH = 20;

export default function ManualInputs({ API, domains }) {
  const [domain, setDomain] = useState('D1');
  const [allControls, setAllControls] = useState([]);
  const [edits, setEdits] = useState({});
  const [saved, setSaved] = useState(false);
  const [showScored, setShowScored] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detailCache, setDetailCache] = useState({});

  const loadControls = useCallback(() => {
    fetch(`${API}/api/controls?domain=${domain}`)
      .then(r => r.json())
      .then(data => {
        setAllControls(data);
        const manual = data.filter(c => !c.auto_scored && c.source === 'Manual');
        const init = {};
        manual.forEach(c => { init[c.id] = { score: c.score || '', notes: c.assessor_notes || '' }; });
        setEdits(init);
      });
    setSaved(false);
  }, [domain, API]);

  useEffect(() => {
    loadControls();
    setDetailCache({});
    setExpandedId(null);
  }, [loadControls]);

  const fetchDetail = async (controlId) => {
    if (detailCache[controlId]) return detailCache[controlId];
    const r = await fetch(`${API}/api/controls/${controlId}/detail`);
    const data = await r.json();
    setDetailCache(prev => ({ ...prev, [controlId]: data }));
    return data;
  };

  const toggleExpand = async (controlId) => {
    if (expandedId === controlId) { setExpandedId(null); return; }
    await fetchDetail(controlId);
    setExpandedId(controlId);
  };

  const manualControls = allControls.filter(c => !c.auto_scored && c.source === 'Manual');
  const unscored = manualControls.filter(c => c.score == null);
  const scored = manualControls.filter(c => c.score != null);
  const displayControls = showScored ? manualControls : unscored.length > 0 ? unscored : manualControls;

  const autoCount = allControls.filter(c => c.auto_scored).length;
  const uploadCount = allControls.filter(c => c.source === 'Upload').length;

  const hasValidationErrors = () => {
    return manualControls.some(c => {
      const e = edits[c.id];
      return e?.score && e.notes && e.notes.length < MIN_NOTES_LENGTH;
    });
  };

  const saveAll = async () => {
    if (hasValidationErrors()) return;
    const toSave = manualControls.filter(c => edits[c.id]?.score);
    await Promise.all(toSave.map(c => {
      const e = edits[c.id];
      const s = parseInt(e.score);
      return fetch(`${API}/api/controls/${c.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          score: s,
          assessor_notes: e.notes,
          status: s >= 3 ? 'provided' : s >= 1 ? (s === 1 ? 'missing' : 'partial') : c.status,
          score_source: 'assessor_input',
        }),
      });
    }));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    loadControls();
  };

  const avgScore = scored.length > 0
    ? (scored.reduce((sum, c) => sum + (c.score || 0), 0) / scored.length).toFixed(1)
    : '—';

  return (
    <div>
      <div className="info-box info-box-blue" style={{marginBottom:16}}>
        <strong>Manual inputs</strong> — score controls based on stakeholder interviews, walkthroughs, observations, and assessor judgment. Click a control row to expand its scoring rubric and guidance.
      </div>

      {/* Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:16}}>
        <div className="stat-card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:22,fontWeight:700,color:'#1565C0',lineHeight:1}}>{manualControls.length}</div>
          <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Manual controls in {domain}</div>
        </div>
        <div className="stat-card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:22,fontWeight:700,color:unscored.length > 0 ? '#F57F17' : '#2E7D32',lineHeight:1}}>{unscored.length}</div>
          <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Not yet scored</div>
        </div>
        <div className="stat-card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:22,fontWeight:700,color:'#2E7D32',lineHeight:1}}>{scored.length}</div>
          <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Already scored</div>
        </div>
        <div className="stat-card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:22,fontWeight:700,color:'#0078D4',lineHeight:1}}>{avgScore}</div>
          <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Avg maturity</div>
        </div>
        <div className="stat-card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:22,fontWeight:700,color:'#B0BAC5',lineHeight:1}}>{autoCount + uploadCount}</div>
          <div style={{fontSize:10,color:'#7A8899',marginTop:2}}>Handled elsewhere</div>
        </div>
      </div>

      {(autoCount > 0 || uploadCount > 0) && (
        <div style={{display:'flex',gap:8,marginBottom:14,fontSize:10,color:'#7A8899',alignItems:'center'}}>
          {autoCount > 0 && (
            <span>{autoCount} control{autoCount!==1?'s':''} in {domain} scored via <span className="chip chip-ms" style={{fontSize:9}}>Auto-scored</span></span>
          )}
          {uploadCount > 0 && (
            <span>{uploadCount} control{uploadCount!==1?'s':''} in {domain} scored via <span className="chip chip-upload" style={{fontSize:9}}>Document Upload</span></span>
          )}
        </div>
      )}

      {/* Domain tabs */}
      <div className="domain-tabs">
        {domains.map(d => (
          <div key={d.id} className={`domain-tab${domain===d.id?' active':''}`} onClick={() => setDomain(d.id)}>
            {d.id}
          </div>
        ))}
      </div>

      {/* Toggle scored/unscored */}
      {scored.length > 0 && unscored.length > 0 && (
        <div style={{display:'flex',gap:8,marginBottom:12}}>
          <button className={`domain-tab${!showScored?' active':''}`} style={{fontSize:10,padding:'3px 10px'}}
            onClick={() => setShowScored(false)}>
            Unscored ({unscored.length})
          </button>
          <button className={`domain-tab${showScored?' active':''}`} style={{fontSize:10,padding:'3px 10px'}}
            onClick={() => setShowScored(true)}>
            All manual ({manualControls.length})
          </button>
        </div>
      )}

      {/* Controls */}
      {displayControls.length === 0 ? (
        <div style={{textAlign:'center',padding:'40px 20px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10}}>
          <div style={{fontSize:13,fontWeight:500,color:'#2E7D32',marginBottom:6}}>All manual controls in {domain} have been scored</div>
          <div style={{fontSize:11,color:'#7A8899'}}>Switch to another domain above, or use the workflow bar to continue.</div>
        </div>
      ) : (
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{width:85}}>Control</th>
                <th>Control statement</th>
                <th style={{width:180}}>Maturity score</th>
                <th style={{width:240}}>Assessor notes</th>
              </tr>
            </thead>
            <tbody>
              {displayControls.map(c => {
                const edit = edits[c.id] || {};
                const detail = detailCache[c.id];
                const isExpanded = expandedId === c.id;
                const selectedScore = edit.score ? parseInt(edit.score) : null;
                const notesTooShort = edit.score && edit.notes && edit.notes.length > 0 && edit.notes.length < MIN_NOTES_LENGTH;

                return (
                <React.Fragment key={c.id}>
                <tr style={{background: c.score != null ? 'transparent' : '#FFFDE7', cursor:'pointer'}}
                    onClick={() => toggleExpand(c.id)}>
                  <td>
                    <span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{c.id}</span>
                    <div style={{fontSize:8,color:'#7A8899',marginTop:2}}>{isExpanded ? '▼' : '▶'} details</div>
                    <br/><span style={{fontSize:10,color:'#7A8899'}}>{c.category}</span>
                  </td>
                  <td style={{fontSize:11,color:'#455A64'}} onClick={e => e.stopPropagation()}>
                    {c.statement}
                    {/* Risk banner for unscored or low-scoring controls */}
                    {(selectedScore == null || selectedScore <= 2) && detail?.risk_statement && (
                      <div style={{
                        background:'#FFF3E0',border:'1px solid #FFE0B2',borderRadius:6,
                        padding:'6px 10px',marginTop:8,fontSize:10,color:'#E65100',lineHeight:1.4
                      }}>
                        <strong>Risk:</strong> {detail.risk_statement}
                      </div>
                    )}
                    {/* Related control pills */}
                    {detail?.related_controls?.length > 0 && (
                      <div style={{marginTop:6,display:'flex',gap:4,flexWrap:'wrap'}}>
                        <span style={{fontSize:9,color:'#7A8899',marginRight:2}}>Related:</span>
                        {detail.related_controls.map(rc => {
                          const relatedControl = allControls.find(x => x.id === rc);
                          const relScore = relatedControl?.score;
                          return (
                            <span key={rc} className="pill" style={{
                              fontSize:9,padding:'1px 6px',cursor:'default',
                              background: relScore ? `${scoreColor(relScore)}15` : '#F5F5F5',
                              color: relScore ? scoreColor(relScore) : '#7A8899',
                              border: `1px solid ${relScore ? scoreColor(relScore) : '#E0E0E0'}`
                            }} title={relatedControl ? `${rc}: score ${relScore ?? 'unscored'} (${relatedControl.source})` : rc}>
                              {rc}{relScore != null ? ` (${relScore})` : ''}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    {/* Score radio buttons */}
                    <div style={{display:'flex',flexDirection:'column',gap:3}}>
                      {SCORE_LABELS.map(sl => (
                        <label key={sl.value} style={{
                          display:'flex',alignItems:'center',gap:6,cursor:'pointer',
                          fontSize:10,padding:'2px 4px',borderRadius:4,
                          background: selectedScore === sl.value ? `${sl.color}12` : 'transparent'
                        }}>
                          <input type="radio" name={`score-${c.id}`}
                            checked={selectedScore === sl.value}
                            onChange={() => setEdits(p => ({...p, [c.id]:{...p[c.id], score: String(sl.value)}}))}
                            style={{margin:0}}
                          />
                          <span style={{
                            fontWeight: selectedScore === sl.value ? 600 : 400,
                            color: selectedScore === sl.value ? sl.color : '#455A64'
                          }}>
                            {sl.value} — {sl.label}
                          </span>
                        </label>
                      ))}
                      {selectedScore && (
                        <button style={{
                          fontSize:9,color:'#7A8899',background:'none',border:'none',cursor:'pointer',
                          textAlign:'left',padding:'2px 4px'
                        }}
                          onClick={() => setEdits(p => ({...p, [c.id]:{...p[c.id], score: ''}}))}>
                          Clear
                        </button>
                      )}
                    </div>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <textarea
                      value={edit.notes || ''}
                      onChange={e => setEdits(p => ({...p, [c.id]:{...p[c.id], notes:e.target.value}}))}
                      placeholder="Evidence reviewed, observations, justification... (min 20 chars)"
                      style={{
                        minHeight:60,
                        borderColor: notesTooShort ? '#C62828' : undefined
                      }}
                    />
                    {notesTooShort && (
                      <div style={{fontSize:9,color:'#C62828',marginTop:2}}>
                        {edit.notes.length}/{MIN_NOTES_LENGTH} chars — minimum {MIN_NOTES_LENGTH} required
                      </div>
                    )}
                    {edit.notes && edit.notes.length >= MIN_NOTES_LENGTH && (
                      <div style={{fontSize:9,color:'#2E7D32',marginTop:2}}>
                        {edit.notes.length} chars
                      </div>
                    )}
                  </td>
                </tr>

                {/* Expanded rubric panel */}
                {isExpanded && detail && (
                  <tr>
                    <td colSpan={4} style={{background:'#F8F9FA',padding:'12px 16px'}}>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                        {/* Scoring rubric */}
                        <div>
                          <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Scoring Rubric</div>
                          {detail.scoring_guidance && Object.keys(detail.scoring_guidance).length > 0 ? (
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
                              <tbody>
                                {Object.entries(detail.scoring_guidance).map(([level, text]) => {
                                  const lvl = parseInt(level);
                                  const isSelected = selectedScore === lvl;
                                  return (
                                    <tr key={level} style={{
                                      background: isSelected ? `${scoreColor(lvl)}10` : 'transparent',
                                      borderLeft: isSelected ? `3px solid ${scoreColor(lvl)}` : '3px solid transparent'
                                    }}>
                                      <td style={{padding:'4px 8px',fontWeight:600,color:scoreColor(lvl),width:24,verticalAlign:'top'}}>{level}</td>
                                      <td style={{padding:'4px 8px',color:'#455A64',lineHeight:1.4}}>{text}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          ) : (
                            <div style={{fontSize:10,color:'#7A8899',fontStyle:'italic'}}>No scoring guidance available for this control.</div>
                          )}
                        </div>

                        {/* Evidence + sub-controls */}
                        <div>
                          {detail.evidence_required && (
                            <div style={{marginBottom:12}}>
                              <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:4}}>Evidence Required</div>
                              <div style={{fontSize:10,color:'#455A64',lineHeight:1.5,background:'#fff',padding:'8px 10px',borderRadius:6,border:'1px solid #E8ECF0'}}>
                                {detail.evidence_required}
                              </div>
                            </div>
                          )}
                          {detail.sub_controls?.length > 0 && (
                            <div>
                              <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:4}}>Sub-controls</div>
                              {detail.sub_controls.map(sc => (
                                <div key={sc.id} style={{
                                  fontSize:10,padding:'6px 10px',marginBottom:4,
                                  background:'#fff',borderRadius:6,border:'1px solid #E8ECF0'
                                }}>
                                  <span style={{fontWeight:600,color:'#0078D4'}}>{sc.id}</span>
                                  <span style={{color:'#455A64',marginLeft:6}}>{sc.statement}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Save + progress */}
      {displayControls.length > 0 && (
        <div style={{marginTop:12,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
          <button className="btn-primary" onClick={saveAll} disabled={hasValidationErrors()}>
            Save scores
          </button>
          {hasValidationErrors() && (
            <span style={{fontSize:11,color:'#C62828'}}>Fix notes validation errors before saving</span>
          )}
          {saved && <span style={{fontSize:11,color:'#2E7D32'}}>Scores saved</span>}
          <span style={{fontSize:11,color:'#7A8899',marginLeft:8}}>
            {scored.length}/{manualControls.length} manual controls in {domain} scored
          </span>
          {/* Progress bar */}
          <div style={{flex:1,minWidth:100,maxWidth:200,height:6,background:'#E8ECF0',borderRadius:3,overflow:'hidden'}}>
            <div style={{
              width: manualControls.length > 0 ? `${(scored.length/manualControls.length)*100}%` : '0%',
              height:'100%',background:'#2E7D32',borderRadius:3,transition:'width 0.3s'
            }}/>
          </div>
        </div>
      )}
    </div>
  );
}
